import { applyNodeChanges, Node, NodeChange, XYPosition } from 'reactflow';

import { ImmerGet, ImmerSet } from '../types';

import {
    BotModuleData,
    CalendarModuleBotModuleData,
    CustomInternalModuleBotModuleData,
    EditableBotVersionSlice,
    NodeType,
} from '@/domain/bot';
import { pushUndoAction } from 'utils/bot/state/editable-bot-fns';
import { autoInsertIntoLinearGraph } from 'utils/reactflow/auto-insert-into-linear-graph';
import { createNewEdgesOnNodeDelete } from 'utils/reactflow/edges/create-new-edges-on-delete';
import { createNodeReactFlow } from 'utils/reactflow/nodes/create-node';
import {
    getLayoutedElements,
    nodeHeight,
    nodeWidth,
} from 'utils/reactflow/nodes/get-layouted-elements';
import {
    createNewNodeId,
    getInsertPosition,
    getNodeSuccessors,
    shiftSuccessors,
} from 'utils/reactflow/nodes/on-node-add';

/**
 * Creates ReactFlow node-related action handlers.
 */
export const createReactflowNodesActions = (
    set: ImmerSet,
    get: ImmerGet,
): Pick<
    EditableBotVersionSlice,
    | 'onNodesChange'
    | 'onNodesRegeneration'
    | 'onNodeAdd'
    | 'onNodesRemove'
    | 'updateNodePositions'
    | 'setNode'
    | 'onValidationChange'
    | 'unselectAllNodes'
    | 'onLayout'
    | 'onSelectionChange'
    | 'setEditingBotFromFlowEditor'
> => ({
    onNodesChange: (changes: NodeChange[]) => {
        setTimeout(() => {
            set((state) => {
                state.editingBot.nodesReactFlow = applyNodeChanges(
                    changes,
                    get().editingBot.nodesReactFlow,
                );
            });
        }, 2);
    },

    onNodesRegeneration: (newNodes, newEdges) => {
        set((state) => {
            state.editingBot.nodesReactFlow = newNodes;
            state.editingBot.edgesReactFlow = newEdges;
            state.changesInBotVersion = true;
            pushUndoAction(state, get);
        });
        get().onLayout();
    },

    onNodeAdd: (nodeType: NodeType) => {
        const NEW_NODE_OFFSET = 125;

        const nodesReactFlow = get().editingBot.nodesReactFlow;
        const selectedHandle = get().editingBot.selectedHandle;
        const nodeId = createNewNodeId(nodeType, nodesReactFlow);

        const { selectedNodePosition, isLast, selectedNode } =
            getInsertPosition(nodesReactFlow);

        const newNode = createNodeReactFlow(
            nodeId,
            nodeType,
            undefined,
            undefined,
            selectedNodePosition.x,
            selectedNodePosition.y + (isLast ? 0 : NEW_NODE_OFFSET),
        );
        newNode.selected = true;
        const newEdges = autoInsertIntoLinearGraph(
            nodesReactFlow,
            get().editingBot.edgesReactFlow,
            nodeId,
            nodeType as any,
            selectedHandle,
        );

        set((state) => {
            pushUndoAction(state, get);

            state.editingBot.nodesReactFlow.forEach(
                (node) => (node.selected = false),
            );
            state.editingBot.nodesReactFlow.splice(1, 0, newNode);
            state.addedNodePosition = newNode.position.y;

            if (newEdges) {
                state.editingBot.edgesReactFlow = newEdges;
            }
        });

        const shiftAmount = NEW_NODE_OFFSET + 50;
        const successors = getNodeSuccessors(
            nodeId,
            get().editingBot.edgesReactFlow,
        );

        if (newEdges) {
            shiftSuccessors(
                successors,
                shiftAmount,
                nodesReactFlow,
                get().updateNodePositions,
                newEdges,
                selectedNode.id,
            );
        }
        get().setSelectedHandle(undefined);
    },

    onNodesRemove: (ids: string[]) => {
        const currentNodes = get().editingBot.nodesReactFlow;
        const currentEdges = get().editingBot.edgesReactFlow;

        let deleted: Node<BotModuleData>[] = [];

        ids.forEach((id) => {
            const deletedNode = currentNodes.find((node) => node.id === id);
            if (deletedNode) {
                deleted.push(deletedNode);
            }
            return;
        });

        const newEdges = deleted.reduce(
            (currentEdges, node) =>
                createNewEdgesOnNodeDelete(currentEdges, node, currentNodes),
            currentEdges,
        );

        const newNodes = get().editingBot.nodesReactFlow.filter(
            (node) => !ids.includes(node.id),
        );

        set((state) => {
            pushUndoAction(state, get);

            state.editingBot.edgesReactFlow = newEdges;
            state.editingBot.nodesReactFlow = newNodes;
            state.changesInBotVersion = true;
        });
    },

    updateNodePositions: (shiftedPositions: Record<string, XYPosition>) => {
        set((state) => {
            state.editingBot.nodesReactFlow =
                state.editingBot.nodesReactFlow.map((node) => {
                    if (shiftedPositions[node.id]) {
                        return {
                            ...node,
                            position: shiftedPositions[node.id],
                        };
                    }
                    return node;
                });
        });
    },

    setNode: (nodeIndex, params, source?, typeOverride?) => {
        set((state) => {
            const node = state.editingBot.nodesReactFlow[nodeIndex];

            if (node?.data) {
                if (node.data.type === 'CalendarModule') {
                    if (source) {
                        (node.data as CalendarModuleBotModuleData).source = {
                            ...source,
                        };
                    }
                } else {
                    if ('params' in node.data) {
                        node.data.params = params;
                    }

                    if (source && 'source' in node.data) {
                        node.data.source = { ...source };
                    }
                }

                if (typeOverride && node.data.type === 'CustomInternalModule') {
                    (
                        node.data as CustomInternalModuleBotModuleData
                    ).typeOverride = typeOverride;
                }
            }

            state.changesInBotVersion = true;
        });
    },

    onValidationChange: (isValid: boolean, nodeIndex: number) => {
        set((state) => {
            state.editingBot.nodesReactFlow[nodeIndex].data.valid = isValid;
            state.editingBot.nodesReactFlow[nodeIndex].selected = true;
        });
    },

    unselectAllNodes: () => {
        set((state) => {
            state.editingBot.nodesReactFlow.forEach((node) => {
                node.selected = false;
            });
        });
    },

    onLayout: () => {
        const nodesReactFlow = get().editingBot.nodesReactFlow;

        const dagreGraph = getLayoutedElements(
            nodesReactFlow,
            get().editingBot.edgesReactFlow,
        );

        nodesReactFlow.forEach((node, index) =>
            set((state) => {
                state.editingBot.nodesReactFlow[index].position = {
                    x: dagreGraph.node(node.id).x - nodeWidth / 2,
                    y: dagreGraph.node(node.id).y - nodeHeight / 2,
                };
            }),
        );
    },

    onSelectionChange: () => {
        set((state) => {
            pushUndoAction(state, get);
        });
    },

    setEditingBotFromFlowEditor: (nodes, edges) => {
        set((state) => {
            state.editingBot.nodesReactFlow = nodes as any;
            state.editingBot.edgesReactFlow = edges;
            state.changesInBotVersion = true;
        });
    },
});
